import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShoppingBag, X, Plus, Minus, Trash2, ArrowRight, Gift, Truck } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { api, formatCLP } from "../lib/api";

export const CartFloat = () => {
  const { items, updateQty, removeItem, totalCount, totalPrice } = useCart();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const drawerRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Auto-close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (open && drawerRef.current && !drawerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Hide on cart page
  if (location.pathname === "/carrito") return null;

  const giftThreshold = settings?.gift_threshold || 20000;
  const deliveryThreshold = settings?.delivery_threshold || 30000;
  const total = totalPrice || 0;
  const giftPct = Math.min(100, (total / giftThreshold) * 100);
  const deliveryPct = Math.min(100, (total / deliveryThreshold) * 100);
  const giftReached = total >= giftThreshold;
  const deliveryReached = total >= deliveryThreshold;

  return (
    <>
      <button
        onClick={() => setOpen((s) => !s)}
        data-testid="cart-float-btn"
        aria-label="Carrito rápido"
        className="fixed bottom-44 right-6 z-40 group"
      >
        <span className="relative flex items-center justify-center h-14 w-14 rounded-full bg-mint-deep text-white shadow-lg border-2 border-black hover:scale-110 hover:-rotate-6 transition-transform duration-300">
          <ShoppingBag className="h-6 w-6" />
          {totalCount > 0 && (
            <span data-testid="cart-float-badge" className="absolute -top-1 -right-1 h-6 min-w-[24px] px-1 rounded-full bg-yolk text-black text-[11px] font-bold flex items-center justify-center border-2 border-black">
              {totalCount}
            </span>
          )}
        </span>
      </button>

      {/* Backdrop */}
      <div className={`fixed inset-0 z-40 bg-black/30 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} />

      {/* Side drawer */}
      <aside
        ref={drawerRef}
        data-testid="cart-float-drawer"
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[420px] bg-cream border-l-2 border-black shadow-2xl flex flex-col transform transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black bg-yolk">
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-black" />
            <h3 className="font-heading font-extrabold text-black tracking-tight">Tu carrito</h3>
            {totalCount > 0 && <span className="text-xs bg-black text-yolk px-2 py-0.5 rounded-full font-bold">{totalCount}</span>}
          </div>
          <button onClick={() => setOpen(false)} data-testid="cart-float-close" className="h-9 w-9 rounded-full hover:bg-black/10 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingBag className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm">Tu carrito está vacío</p>
              <button onClick={() => { setOpen(false); navigate("/3d"); }} data-testid="float-empty-go" className="btn-chunky mt-6 inline-flex items-center gap-2 bg-mint-deep text-white px-5 py-2.5 text-xs">
                Explorar figuras
              </button>
            </div>
          ) : (
            <>
              {/* Items */}
              <div className="space-y-3 mb-5">
                {items.map((it) => (
                  <div key={it.product_id} data-testid={`float-item-${it.product_id}`} className="flex gap-3 bg-white border-2 border-black rounded-2xl p-2.5">
                    <div className="h-16 w-16 bg-mint-light rounded-xl overflow-hidden border-2 border-black shrink-0">
                      {it.image_url && <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-heading text-sm font-bold leading-snug line-clamp-2">{it.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{formatCLP(it.price)}</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="inline-flex items-center border-2 border-black rounded-full overflow-hidden bg-white">
                          <button onClick={() => updateQty(it.product_id, it.quantity - 1)} data-testid={`float-decrease-${it.product_id}`} className="h-6 w-6 flex items-center justify-center hover:bg-yolk-light"><Minus className="h-3 w-3" /></button>
                          <span className="w-7 text-center text-xs font-bold">{it.quantity}</span>
                          <button onClick={() => updateQty(it.product_id, it.quantity + 1)} data-testid={`float-increase-${it.product_id}`} className="h-6 w-6 flex items-center justify-center hover:bg-yolk-light"><Plus className="h-3 w-3" /></button>
                        </div>
                        <button onClick={() => removeItem(it.product_id)} data-testid={`float-remove-${it.product_id}`} className="text-gray-400 hover:text-red-500 ml-auto">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Thermometer (same as main cart) */}
              <div className="bg-white border-2 border-black rounded-2xl p-4" data-testid="cart-float-thermometer">
                <h4 className="font-heading font-bold text-brand-ink mb-3 text-xs uppercase tracking-[0.18em]">¡Sigue sumando! 🎁</h4>

                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Gift className="h-3.5 w-3.5 text-yolk" />
                    <span className="text-[11px] font-bold text-brand-ink">Regalo sorpresa GRATIS</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 border-2 border-black rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-500 ${giftReached ? "bg-yolk" : "bg-yolk/70"}`} style={{ width: `${giftPct}%` }} />
                  </div>
                  <p className="text-[10px] mt-1 text-gray-600">
                    {giftReached ? "✅ ¡Lo conseguiste!" : `Faltan ${formatCLP(giftThreshold - total)}`}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Truck className="h-3.5 w-3.5 text-mint-deep" />
                    <span className="text-[11px] font-bold text-brand-ink">Delivery GRATIS en Copiapó</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 border-2 border-black rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-500 ${deliveryReached ? "bg-mint-deep" : "bg-mint"}`} style={{ width: `${deliveryPct}%` }} />
                  </div>
                  <p className="text-[10px] mt-1 text-gray-600">
                    {deliveryReached ? "✅ ¡Envío gratis!" : `Faltan ${formatCLP(deliveryThreshold - total)}`}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="p-4 border-t-2 border-black bg-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.18em] text-gray-500 font-bold">Total</span>
              <span className="font-heading text-xl font-extrabold text-mint-deep">{totalPrice !== null ? formatCLP(totalPrice) : "A cotizar"}</span>
            </div>
            <button onClick={() => { setOpen(false); navigate("/carrito"); }} data-testid="float-go-cart" className="btn-chunky w-full inline-flex items-center justify-center gap-2 bg-mint-deep text-white py-3 text-sm">
              Ir al carrito <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartFloat;
